import React from "react";
import { canLiveTrack, formatTime, gpsAge, hasLocation, initials, isStale, tripStage } from "./trackingRules";

/**
 * Right-side panel of the selected vehicle, docked beside the map (a bottom sheet on phones): status, driver, live GPS,
 * trip details, and the Live Tracking / Center on map actions.
 */
export default function VehicleDetailPanel({ vehicle: v, onClose, onLiveTracking, onCenter }) {
  const stage = tripStage(v);
  const located = hasLocation(v);
  const stale = isStale(v);
  return (
    <aside className={`tc-info-bar${stage?.key === "cancelled" ? " tc-info-bar-cancelled" : ""}`} aria-label={`Vehicle ${v.vehicleNo}`}>
      <button type="button" className="tc-info-close" onClick={onClose} aria-label="Close">
        <i className="fas fa-times" aria-hidden="true" />
      </button>
      <div className="tc-info-top">
        <div className="tc-info-id">
          <span className="tc-status-badge tc-badge-orange">{v.tripStatusDescription}</span>
          <span className="tc-info-vehicle-chip">
            <i className="fas fa-car" aria-hidden="true" />
            {v.vehicleNo}
          </span>
          {stage && (
            <span className={`tc-info-stage tc-info-stage-${stage.key}`}>
              <i className={`fas ${stage.icon}`} aria-hidden="true" />
              {stage.label}
              {stage.time ? ` · ${stage.time}` : ""}
            </span>
          )}
        </div>
        <div className="tc-info-driver">
          <div className="tc-info-avatar">{initials(v.driverName) || <i className="fas fa-user" aria-hidden="true" />}</div>
          <div className="tc-info-driver-text">
            <span className="tc-info-driver-name">{v.driverName}</span>
            {v.driverMoblieNo && (
              <a className="tc-info-driver-mobile" href={`tel:${v.driverMoblieNo}`}>
                <i className="fas fa-phone-alt" aria-hidden="true" /> {v.driverMoblieNo}
              </a>
            )}
          </div>
        </div>
      </div>

      {located ? (
        <div className="trk-section">
          <h3 className="trk-section__title">Live</h3>
          <div className="tc-info-stats">
            <div className="tc-info-stat">
              <span className="tc-info-stat-val">{Number(v.speed ?? 0).toFixed(1)}</span>
              <span className="tc-info-stat-lbl">km/h</span>
            </div>
            <div className="tc-info-stat">
              <span className="tc-info-stat-val">{v.altitude != null ? Math.round(v.altitude) : "--"}</span>
              <span className="tc-info-stat-lbl">m alt</span>
            </div>
            <div className="tc-info-stat tc-info-stat-time trk-gps-time" title="Time of the last GPS position">
              <i className="fas fa-clock" aria-hidden="true" />
              <span>
                {formatTime(v.gpsDateTime) || "--"}
                {v.gpsDateTime && <small>{gpsAge(v.gpsDateTime)}</small>}
              </span>
            </div>
          </div>
          {stale && (
            <div className="tc-info-warning trk-stale-warning">
              <i className="fas fa-exclamation-triangle" aria-hidden="true" />
              GPS not updated since {formatTime(v.gpsDateTime)} ({gpsAge(v.gpsDateTime)}): the vehicle may no longer be at this place.
            </div>
          )}
        </div>
      ) : (
        <div className="tc-info-warning">
          <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          Vehicle geo location not found
        </div>
      )}

      <div className="trk-section">
        <h3 className="trk-section__title">Trip</h3>
        <div className="tc-info-bottom">
          <div className="tc-info-chip">
            <i className="fas fa-user" aria-hidden="true" />
            <b>Crew</b>
            <span className="tc-info-chip-value">{v.paxName}</span>
          </div>
          <div className="tc-info-chip">
            <i className="fas fa-clock" aria-hidden="true" />
            <b>Pickup</b>
            <span className="tc-info-chip-value">{formatTime(v.pickupDateTime)}</span>
          </div>
          <div className="tc-info-chip">
            <i className="fas fa-map-marker-alt" aria-hidden="true" />
            <b>Pick-up</b>
            <span className="tc-info-chip-value">{v.pickupLocation}</span>
          </div>
          <div className="tc-info-chip">
            <i className="fas fa-flag-checkered" aria-hidden="true" />
            <b>Dropoff</b>
            <span className="tc-info-chip-value">{v.dropoffLocation}</span>
          </div>
          <div className="tc-info-chip">
            <i className="fas fa-hashtag" aria-hidden="true" />
            <b>Ride</b>
            <span className="tc-info-chip-value">
              {v.rideId}
              {v.transactionId ? ` · ${v.transactionId}` : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="trk-panel-actions">
        {canLiveTrack(v) && (
          <button type="button" className="tc-live-track-btn" onClick={onLiveTracking}>
            <i className="fas fa-satellite-dish" aria-hidden="true" />
            Live Tracking
          </button>
        )}
        {located && (
          <button type="button" className="tc-live-track-btn trk-center-btn" onClick={onCenter}>
            <i className="fas fa-crosshairs" aria-hidden="true" />
            Center on map
          </button>
        )}
      </div>
    </aside>
  );
}
